// TabsContext — Chrome-style multi-tab navigation for the Radiants ERP home screen.
//
// Behaviour:
//   • Sidebar click → `openTab(path)`. If the path is already an open tab, just activates it.
//     Otherwise it appends a new tab (max 12). The browser URL is updated via react-router
//     so refresh / back-forward continue to work naturally.
//   • Form actions (e.g. "Add Item") call `openFormTab({ path, label, icon, render, parent })`
//     to spawn a *dynamic* tab whose render function is stored inline (not in TAB_REGISTRY).
//   • Close X on a tab → removes the tab; if it was active, switches to the neighbour
//     (or the dynamic tab's declared `parent` path, when set).
//   • Components for every open tab stay MOUNTED behind the scenes (rendered with
//     display:none) — so form state / scroll positions / filters are preserved when
//     toggling between tabs.
//
// The URL is the source of truth for the "active" tab. The `openTabs` array is the
// list of currently-mounted tabs.

import { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { TAB_REGISTRY, MAX_TABS, DEFAULT_TAB_PATH } from "@/lib/tabRegistry";

const TabsContext = createContext(null);

export function useTabs() {
  const ctx = useContext(TabsContext);
  if (!ctx) throw new Error("useTabs must be used within <TabsProvider>");
  return ctx;
}

const buildTab = (path) => {
  const cfg = TAB_REGISTRY[path];
  if (!cfg) return null;
  // Tabs whose page auto-spawns a "New X" form on landing are opened *hidden*
  // — the user only sees the form tab. Clicking "View List" inside the form
  // (or re-clicking the sidebar item) reveals the list tab.
  return {
    path,
    label: cfg.label,
    icon: cfg.icon,
    dynamic: false,
    hidden: !!cfg.autoSpawnsForm,
  };
};

export function TabsProvider({ children }) {
  const location = useLocation();
  const navigate = useNavigate();

  // Start fresh on every mount (login → Dashboard). Persistence across sessions is
  // intentionally omitted — keeps things predictable.
  const [openTabs, setOpenTabs] = useState(() => {
    const initial = [buildTab(DEFAULT_TAB_PATH)].filter(Boolean);
    // If we land on a non-default path (e.g. deep link / refresh), add it too.
    const path = window.location.pathname;
    if (path !== DEFAULT_TAB_PATH && TAB_REGISTRY[path]) {
      initial.push(buildTab(path));
    }
    return initial;
  });
  // Mirror openTabs / activePath into refs so callbacks captured by long-lived
  // closures (e.g. form tab `onClose` handlers) always read the LATEST values
  // instead of values captured at openFormTab time.
  const openTabsRef = useRef(openTabs);
  openTabsRef.current = openTabs;

  const activePath = location.pathname;
  const activePathRef = useRef(activePath);
  activePathRef.current = activePath;

  // On INITIAL mount: if the URL is neither a registered tab path nor a (just-recovered)
  // dynamic-tab path, try to redirect to the closest registered parent (so a hard refresh
  // on /estimates/new lands on the Estimates list), falling back to the default tab.
  // Guarded by a ref so StrictMode's double-effect doesn't trip the redirect AFTER
  // an autoSpawn has already changed the URL to a dynamic path.
  const initRedirectDoneRef = useRef(false);
  useEffect(() => {
    if (initRedirectDoneRef.current) return;
    initRedirectDoneRef.current = true;
    const path = window.location.pathname;
    if (TAB_REGISTRY[path]) return;
    // Try to find a registered parent (e.g. /estimates/new → /estimates).
    const segs = path.split("/").filter(Boolean);
    while (segs.length > 1) {
      segs.pop();
      const parent = "/" + segs.join("/");
      if (TAB_REGISTRY[parent]) {
        navigate(parent, { replace: true });
        return;
      }
    }
    navigate(DEFAULT_TAB_PATH, { replace: true });
    // Run once on mount only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Whenever the URL changes to a known REGISTERED path, ensure the tab is in `openTabs`.
  // (Dynamic tabs are URL-less by design — they're opened via openFormTab.)
  useEffect(() => {
    if (!TAB_REGISTRY[activePath]) return;
    setOpenTabs((prev) => {
      if (prev.some((t) => t.path === activePath)) return prev;
      // Beyond the limit? Drop the oldest non-default tab to make room (safety net only —
      // user-initiated clicks go through `openTab` which enforces the limit visibly).
      if (prev.length >= MAX_TABS) {
        const droppable = prev.findIndex((t) => t.path !== DEFAULT_TAB_PATH);
        const trimmed = [...prev];
        if (droppable !== -1) trimmed.splice(droppable, 1);
        return [...trimmed, buildTab(activePath)];
      }
      return [...prev, buildTab(activePath)];
    });
  }, [activePath, navigate]);

  const openTab = useCallback(
    (path) => {
      if (!TAB_REGISTRY[path]) {
        // Unknown path — let react-router handle it (404 / redirect).
        navigate(path);
        return;
      }
      const openTabsNow = openTabsRef.current;
      const activePathNow = activePathRef.current;
      // If this module auto-spawns a form and an associated form tab is already open,
      // switch to that form tab instead of revealing the list (per UX: "only the new
      // voucher should open first").
      const cfg = TAB_REGISTRY[path];
      if (cfg.autoSpawnsForm) {
        const formTab = openTabsNow.find((t) => t.dynamic && t.parent === path);
        if (formTab) {
          if (formTab.path !== activePathNow) navigate(formTab.path);
          return;
        }
      }
      const existingIdx = openTabsNow.findIndex((t) => t.path === path);
      const alreadyOpen = existingIdx !== -1;
      if (!alreadyOpen && openTabsNow.length >= MAX_TABS) {
        toast.error("Maximum 12 tabs can be opened. Please close an existing tab to open a new section.");
        return;
      }
      // If the tab is open but hidden (autoSpawnsForm flow), reveal it now since
      // the user explicitly clicked the sidebar item again.
      if (alreadyOpen && openTabsNow[existingIdx].hidden) {
        setOpenTabs((prev) => prev.map((t, i) => i === existingIdx ? { ...t, hidden: false } : t));
      }
      if (path !== activePathNow) navigate(path);
    },
    [navigate],
  );

  /**
   * Activate an already-open tab — used by TabBar clicks. Unlike `openTab`,
   * this does NOT apply any auto-spawn-form redirect: the user clicked THIS
   * specific tab, so we navigate to exactly THIS path.
   */
  const activateTab = useCallback(
    (path) => {
      if (path !== activePathRef.current) navigate(path);
    },
    [navigate],
  );

  /**
   * Reveal a hidden tab (autoSpawnsForm modules start hidden) and switch to it.
   * Called by form "View List" buttons. Uses refs to read latest state so
   * closures captured at form-open time still see the right activePath.
   */
  const revealTab = useCallback(
    (path) => {
      const openTabsNow = openTabsRef.current;
      const activePathNow = activePathRef.current;
      const idx = openTabsNow.findIndex((t) => t.path === path);
      if (idx === -1) {
        // Tab not open yet — add it (visible) and navigate.
        setOpenTabs((prev) => [...prev, { ...buildTab(path), hidden: false }].filter(Boolean));
      } else if (openTabsNow[idx].hidden) {
        setOpenTabs((prev) => prev.map((t, i) => i === idx ? { ...t, hidden: false } : t));
      }
      if (path !== activePathNow) navigate(path);
    },
    [navigate],
  );

  /**
   * Open a *dynamic* tab — a tab whose render function lives inline (not in
   * TAB_REGISTRY). Used by "Add X" / "Edit X" actions across the app so every
   * form gets its own switchable tab instead of a modal overlay.
   *
   * Args (object):
   *   path   — unique path string (e.g. `/items/new`, `/items/edit/${id}`). Used as
   *            the tab key + URL.
   *   label  — visible tab label (e.g. "New Item", "Edit Item · ABC-123").
   *   icon   — lucide icon component (optional).
   *   render — () => ReactNode. Renders the form/page inside the work-area.
   *   parent — optional path to navigate to when this dynamic tab is closed
   *            (defaults to the previously-active tab).
   */
  const openFormTab = useCallback(
    ({ path, label, icon, render, parent }) => {
      if (!path || !label || typeof render !== "function") {
        // eslint-disable-next-line no-console
        console.warn("openFormTab requires { path, label, render }");
        return;
      }
      setOpenTabs((prev) => {
        const existing = prev.findIndex((t) => t.path === path);
        if (existing !== -1) {
          // Tab already open — refresh its render (in case label/icon updated) and activate.
          const next = [...prev];
          next[existing] = { ...next[existing], label, icon, render, parent };
          return next;
        }
        if (prev.length >= MAX_TABS) {
          toast.error("Maximum 12 tabs can be opened. Please close an existing tab to open a new section.");
          return prev;
        }
        return [...prev, { path, label, icon, render, parent, dynamic: true }];
      });
      if (path !== activePath) {
        // The URL path is registered as "unknown" by react-router, but TabContent picks it
        // up via the tab object itself. We still navigate so the URL bar reflects it
        // (and the back/forward buttons work).
        navigate(path);
      }
    },
    [activePath, navigate],
  );

  const closeTab = useCallback(
    (path) => {
      // Read LATEST openTabs / activePath via refs (closures captured by long-lived
      // form `onClose` handlers may otherwise see stale snapshots from when the
      // form tab was first opened).
      const openTabsNow = openTabsRef.current;
      const activePathNow = activePathRef.current;
      const idx = openTabsNow.findIndex((t) => t.path === path);
      if (idx === -1) return;
      const closing = openTabsNow[idx];
      const next = openTabsNow.filter((t) => t.path !== path);
      let pendingNavigate = null;
      let nextOpenTabs = next;
      if (path === activePathNow) {
        if (next.length === 0) {
          pendingNavigate = DEFAULT_TAB_PATH;
          nextOpenTabs = [buildTab(DEFAULT_TAB_PATH)].filter(Boolean);
        } else {
          const fallback = closing?.parent && next.some((t) => t.path === closing.parent)
            ? next.find((t) => t.path === closing.parent)
            : next[Math.min(idx, next.length - 1)];
          // If the resolved fallback tab is hidden (autoSpawnsForm parent that was
          // never revealed), navigate to the default tab instead — otherwise the
          // user sees a hidden tab become active with no visible bar entry.
          pendingNavigate = fallback.hidden ? DEFAULT_TAB_PATH : fallback.path;
        }
      }
      setOpenTabs(nextOpenTabs);
      if (pendingNavigate) navigate(pendingNavigate);
    },
    [navigate],
  );

  const value = { openTabs, activePath, openTab, activateTab, closeTab, openFormTab, revealTab };
  return <TabsContext.Provider value={value}>{children}</TabsContext.Provider>;
}
